/**
 * リダイレクトを使わないログイン経路（One Tap の signInWithCredential /
 * preview 環境フォールバックの signInWithPopup）向けの、ログイン後処理。
 *
 * 既存のソーシャルログインは firebase-lib.js の getRedirectResult ハンドラが
 * varuna `endPoints.login` への送信（confirm_code・アフィリエイト・OEM 連携等）と
 * handleDigramExamId を行うが、redirect を経由しないログインはそこを通らない。
 * 本 composable は同ハンドラと同じ内容を再現する（firebase-lib.js は 1000 行超の
 * 分解義務があるため手を入れない。分割時には本実装と firebase-lib 内の複製 3 箇所を
 * 1 つに統合すること）。
 *
 * 意図的な差分:
 * - `login_redirect_function` の emit は省略（現在購読者が存在しないデッドイベント）
 * - FormData は varuna 既存 login エンドポイントが form 受けのため踏襲（新規 API ではない）
 */
import type { User } from '@/infra/firebase/auth'
import {
  clearReferralCode,
  loadReferralCode
} from '@/utils/yagiprime/referralVisitor'
import { emitVarunaLoginSynced } from '@/plugins/emitter'
import { getFirebaseClient } from '@/plugins/firebase-lib'

type ClientFacade = {
  isLoginAction?: boolean
  showResultMessage?: (message: string, type: string) => void
  handleDigramExamId?: (user: User) => void
  $cookies?: { get: (key: string) => string | null }
  $site?: { appFlag?: string }
  $deviceLib?: { isMobileDevice?: boolean }
  $oemContext?: { currentOemId?: unknown; currentOemUniqueId?: string }
  $axios?: { post: (url: string, data: FormData) => Promise<unknown> }
  $varuna?: { endPoints?: { login?: string } }
}

export type UseLoginPostProcessReturn = {
  /** 認証成立直後（UserCredential 取得後）に一度だけ呼ぶ */
  runLoginPostProcess: (user: User) => Promise<void>
}

export function useLoginPostProcess(): UseLoginPostProcessReturn {
  async function runLoginPostProcess(user: User): Promise<void> {
    const client = getFirebaseClient() as ClientFacade | null
    if (!client) return

    client.showResultMessage?.(
      user.email ? `ログインしました。：${user.email}` : 'ログインしました。',
      'success'
    )
    client.isLoginAction = true

    const formData = new FormData()
    formData.append('uuid', user.uid)
    formData.append('email', user.email || '')
    formData.append(
      'confirm_code',
      client.$cookies?.get('_TEMPDATA_CONFIRM_CODE') as string
    )
    const athleteUnivId = client.$cookies?.get('_YAGISH_ATHLETE_UNIVID')
    if (athleteUnivId) formData.append('athlete_univ_id', athleteUnivId)
    const affiliateId = client.$cookies?.get('_YAGISH_AFFILIATE_ID')
    if (affiliateId) formData.append('affiliate_id', affiliateId)
    // 紹介キャンペーン。formDataHelpers 側の複製なので、片方だけだと
    // Google One Tap 経由の登録で紹介が紐付かない
    const referralCode = loadReferralCode()
    if (referralCode) formData.append('referral_code', referralCode)
    formData.append('app_flag', client.$site?.appFlag || '')
    formData.append('site_flag', 'r')
    formData.append('email_verified', user.emailVerified ? '1' : '0')
    formData.append(
      'device_flag',
      client.$deviceLib?.isMobileDevice ? 'SP' : 'PC'
    )

    const ocrSessionId = window.localStorage.getItem('ocr_session_id')
    if (ocrSessionId) formData.append('ocr_session_id', ocrSessionId)

    const oemContext = client.$oemContext
    const isOemEnvironment = !!(oemContext && oemContext.currentOemId)
    formData.append('oem_flg', isOemEnvironment ? 'true' : 'false')
    if (isOemEnvironment && oemContext?.currentOemUniqueId) {
      formData.append('oem_id', oemContext.currentOemUniqueId)
    } else {
      const cookieOemId = client.$cookies?.get('_YAGISH_OEM_UNIQUE_ID')
      if (cookieOemId) formData.append('oem_id', cookieOemId)
    }

    const loginEndpoint = client.$varuna?.endPoints?.login
    if (loginEndpoint && client.$axios) {
      try {
        await client.$axios.post(loginEndpoint, formData)
        // 紹介コードは送ったら使い切る（formDataHelpers 側と同じ理由。
        // 残すと同じブラウザの次のアカウントまで同じ紹介者に紐付く）
        clearReferralCode()
        // 紹介の紐付けはこの POST でサーバー側に確定する。Firebase の認証成功
        // だけを見ると間に合わないので、待っている画面へ知らせる
        emitVarunaLoginSynced()
      } catch (e) {
        console.error('[useLoginPostProcess] login post failed', e)
      }
    }

    client.handleDigramExamId?.(user)
  }

  return { runLoginPostProcess }
}
